<script>
  import { licenseState } from '../stores/license.js';
  import { localDBState, refreshLocalCounts, setLastImportedAt } from '../stores/localdb.js';
  import { appStatus, pushNotice } from '../stores/ui.js';
  import { initVault, unlockVault, lockVault, vaultState, getVaultKey } from '../stores/vault.js';
  import { getClientId, setClientId, getApiKey, setApiKey, getDefaultChannelId, setDefaultChannelId } from '../lib/youtube/config.js';
  import { getTokenStatus } from '../lib/youtube/session.js';
  import { parseExportFile } from '../lib/import/importer.js';
  import { importPayload, exportPayload } from '../lib/import/flow.js';
  import { onMount } from 'svelte';
  import { t, locale as localeStore } from 'svelte-i18n';
  import { applyTheme } from '../lib/theme/applyTheme.js';
  import { LANGUAGE_OPTIONS } from '../i18n/languages.js';
  import { setAppLocale } from '../i18n/index.js';
  import { loadLanguageSetting, saveLanguageSetting } from '../i18n/languageStore.js';
  import { listThemePresets, DEFAULT_THEME_ID } from '../lib/theme/themes.js';
  import { loadThemeSettings, saveThemePreset, saveThemeAccent } from '../lib/theme/themeStore.js';

  let clientId = '';
  let apiKey = '';
  let channelId = '';
  let passphrase = '';
  let tokenStatus = { status: 'locked' };
  let importStatus = '';
  let importProgress = '';

  const themePresets = listThemePresets();
  let themePresetId = DEFAULT_THEME_ID;
  let themeAccent = '';

  let selectedLocale = 'de';

  onMount(async () => {
    appStatus.set({
      primary: 'Settings',
      secondary: 'Privacy, YouTube connection, and your archive'
    });
    initVault();
    clientId = await getClientId() || '';
    apiKey = await getApiKey() || '';
    channelId = await getDefaultChannelId() || '';

    // Theme (load from per-user IndexedDB settings)
    try {
      const theme = await loadThemeSettings();
      themePresetId = theme.presetId;
      themeAccent = theme.accent || '';
      applyTheme(theme);
    } catch (e) {
      // ignore
    }

    // Language
    try {
      selectedLocale = (await loadLanguageSetting()) || $localeStore;
    } catch {
      selectedLocale = $localeStore;
    }

    refreshTokenStatus();
    refreshLocalCounts();
  });

  async function refreshTokenStatus() {
    if (!$vaultState.hasKey) {
      tokenStatus = { status: 'locked' };
      return;
    }
    try {
      const key = getVaultKey();
      tokenStatus = await getTokenStatus(key);
    } catch (error) {
      tokenStatus = { status: 'error', message: error.message };
    }
  }

  async function handleSaveClientId() {
    if (!clientId.trim()) {
      pushNotice('Client ID is required.', 'warning');
      return;
    }
    await setClientId(clientId);
    pushNotice('Client ID saved for this user.', 'success');
  }

  async function handleUnlock() {
    if (!passphrase) {
      pushNotice('Passphrase required.', 'warning');
      return;
    }
    await unlockVault(passphrase);
    passphrase = '';
    pushNotice('Vault unlocked.', 'success');
    await refreshTokenStatus();
  }

  async function handleSaveApi() {
    if (apiKey) await setApiKey(apiKey);
    if (channelId) await setDefaultChannelId(channelId);
    pushNotice('API settings saved for this user.', 'success');
  }

  async function handleThemePresetChange(event) {
    const presetId = event?.target?.value || DEFAULT_THEME_ID;
    themePresetId = await saveThemePreset(presetId);
    applyTheme({ presetId: themePresetId, accent: themeAccent || null });
    pushNotice('Theme preset saved.', 'success');
  }

  async function handleLocaleChange(event) {
    const next = event?.target?.value;
    const normalized = setAppLocale(next);
    selectedLocale = normalized;
    await saveLanguageSetting(normalized);
    pushNotice(`Language saved: ${normalized}`, 'success');
  }

  async function handleAccentChange(event) {
    const value = event?.target?.value || '';
    themeAccent = value;
    await saveThemeAccent(value || null);
    applyTheme({ presetId: themePresetId, accent: value || null });
  }

  async function resetAccent() {
    themeAccent = '';
    await saveThemeAccent(null);
    applyTheme({ presetId: themePresetId, accent: null });
    pushNotice('Accent reset.', 'success');
  }

  function handleLock() {
    lockVault();
    pushNotice('Vault locked.', 'success');
    tokenStatus = { status: 'locked' };
  }

  async function handleImport(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    importStatus = 'Parsing export file...';
    try {
      const payload = await parseExportFile(file);
      importStatus = 'Importing...';
      await importPayload(payload, {
        onProgress: ({ store, current, total }) => {
          importProgress = `${store} (${current}/${total})`;
        }
      });
      importStatus = 'Import complete.';
      importProgress = '';
      await refreshLocalCounts();
      setLastImportedAt(new Date().toISOString());
      pushNotice('Import complete.', 'success');
    } catch (error) {
      importStatus = '';
      importProgress = '';
      pushNotice(error.message || 'Import failed.', 'error');
    }
  }

  async function handleExport() {
    try {
      const payload = await exportPayload();
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `yt-channel-scanner-export-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
      pushNotice('Export created.', 'success');
    } catch (error) {
      pushNotice(error.message || 'Export failed.', 'error');
    }
  }
</script>

<section class="header">
  <div>
    <p class="eyebrow">Settings</p>
    <h1>Control security & storage.</h1>
    <p class="lead">Everything sensitive is encrypted locally. Configure your vault and exports here.</p>
  </div>
</section>

<section class="grid">
  <div class="panel">
    <div class="panel-title">{$t('common.language')}</div>
    <div class="panel-row">
      <span>{$t('common.language')}</span>
      <strong>{LANGUAGE_OPTIONS.find((l) => l.code === selectedLocale)?.label || selectedLocale}</strong>
    </div>
    <select bind:value={selectedLocale} on:change={handleLocaleChange}>
      {#each LANGUAGE_OPTIONS as opt}
        <option value={opt.code}>{opt.label}</option>
      {/each}
    </select>
    <div class="hint">{$t('auth.languageHint')}</div>
  </div>

  <div class="panel">
  <div class="panel-title">{$t('theme.title')}</div>
    <div class="panel-row">
      <span>{$t('theme.preset')}</span>
      <strong>{themePresets.find((p) => p.id === themePresetId)?.label || themePresetId}</strong>
    </div>
    <select bind:value={themePresetId} on:change={handleThemePresetChange}>
      {#each themePresets as preset}
        <option value={preset.id}>{preset.label}</option>
      {/each}
    </select>

    <div class="panel-row">
      <span>{$t('theme.customAccent')}</span>
      <strong>{themeAccent ? themeAccent : $t('theme.presetAccent')}</strong>
    </div>
    <div class="row-actions">
      <input class="color" type="color" bind:value={themeAccent} on:input={handleAccentChange} />
      <button class="secondary" on:click={resetAccent} disabled={!themeAccent}>{$t('theme.resetAccent')}</button>
    </div>
    <div class="hint">{$t('theme.storedLocally')}</div>
  </div>

  <div class="panel">
  <div class="panel-title">Privacy lock</div>
    <div class="panel-row">
      <span>Status</span>
      <strong>{ $vaultState.hasKey ? 'Unlocked' : 'Locked (encrypted)' }</strong>
    </div>
    <input type="password" placeholder="Passphrase" bind:value={passphrase} />
    <div class="row-actions">
      <button class="primary" on:click={handleUnlock}>Unlock vault</button>
      <button class="secondary" on:click={handleLock}>Lock vault</button>
    </div>
  </div>

  <div class="panel">
  <div class="panel-title">YouTube connection</div>
    <div class="panel-row">
    <span>Client ID</span>
      <strong>{ clientId ? 'Configured' : 'Not configured' }</strong>
    </div>
    <input placeholder="OAuth Client ID" bind:value={clientId} />
    <button class="secondary" on:click={handleSaveClientId}>Save</button>
    <input placeholder="YouTube API Key (optional)" bind:value={apiKey} />
    <input placeholder="Channel ID for API-key scan" bind:value={channelId} />
    <div class="hint">API key + Channel ID are required if you don’t connect via OAuth.</div>
    <button class="secondary" on:click={handleSaveApi}>Save API settings</button>
    <div class="panel-row">
      <span>Scopes</span>
      <strong>youtube.readonly</strong>
    </div>
    <div class="panel-row">
    <span>Status</span>
      <strong>
        {#if tokenStatus.status === 'locked'}
          Vault locked
        {:else if tokenStatus.status === 'missing'}
          Not connected
        {:else if tokenStatus.status === 'available'}
          Active
        {:else}
          Error
        {/if}
      </strong>
    </div>
    <button class="secondary" on:click={refreshTokenStatus}>Refresh status</button>
  </div>

  <div class="panel">
  <div class="panel-title">Your archive</div>
    <div class="panel-row">
      <span>Channels</span>
      <strong>{ $localDBState.channelCount }</strong>
    </div>
    <div class="panel-row">
      <span>Videos</span>
      <strong>{ $localDBState.videoCount }</strong>
    </div>
    <button class="secondary">Reset local database</button>
  </div>
</section>

<section class="panel">
  <div class="panel-title">Plan</div>
  <div class="panel-row">
    <span>Plan</span>
    <strong>{ $licenseState.plan }</strong>
  </div>
  <div class="panel-row">
    <span>Trial ends</span>
    <strong>{ $licenseState.trialEndsAt }</strong>
  </div>
  <div class="panel-row">
    <span>Price</span>
    <strong>1 EUR/month billed yearly</strong>
  </div>
  <button class="primary">Manage subscription</button>
</section>

<section class="panel">
  <div class="panel-title">Backup</div>
  <p>Export your archive to keep a backup or move to another device.</p>
  <div class="row-actions">
    <button class="secondary" on:click={handleExport}>Export JSON</button>
    <label class="file-button">
      Restore backup
      <input type="file" accept="application/json" on:change={handleImport} />
    </label>
  </div>
  {#if importStatus}
    <div class="import-status">{importStatus}</div>
  {/if}
  {#if importProgress}
    <div class="import-progress">{importProgress}</div>
  {/if}
</section>

<style>
  .header {
    margin-bottom: 2rem;
  }

  .eyebrow {
    font-size: 0.75rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
  }

  h1 {
    margin: 0.5rem 0 0.75rem;
    font-size: clamp(2rem, 3vw, 2.6rem);
  }

  .lead {
    color: var(--muted);
  }

  .grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    margin-bottom: 1.5rem;
  }

  .panel {
    background: var(--card);
    border-radius: 18px;
    padding: 1.5rem;
    border: 1px solid var(--line);
    display: grid;
    gap: 0.75rem;
  }

  .panel-title {
    font-weight: 700;
  }

  .panel-row {
    display: flex;
    justify-content: space-between;
    color: var(--muted);
  }

  input,
  select {
    padding: 0.6rem 0.8rem;
    border-radius: 12px;
    border: 1px solid var(--line);
    font-size: 0.9rem;
    background: rgba(0, 0, 0, 0.2);
    color: var(--ink);
  }

  input.color {
    width: 56px;
    height: 40px;
    padding: 0;
    border-radius: 12px;
    overflow: hidden;
  }



  .primary,
  .secondary {
    padding: 0.6rem 1.2rem;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    font-weight: 600;
  }

  .primary {
    background: var(--accent);
    color: #fff;
  }

  .secondary {
    background: rgba(255, 255, 255, 0.08);
    color: var(--ink);
    border: 1px solid var(--line);
  }

  .row-actions {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .hint {
    color: var(--muted);
    font-size: 0.85rem;
  }

  .file-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0.6rem 1.2rem;
    border-radius: 999px;
    cursor: pointer;
    font-weight: 600;
    background: rgba(255, 255, 255, 0.08);
    color: var(--ink);
    border: 1px solid var(--line);
  }

  .file-button input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
  }

  .import-status,
  .import-progress {
    color: var(--muted);
    font-size: 0.85rem;
  }
</style>
